import { useWatch } from '@vtex/admin-ui-form'
import {
  getIds,
  usePaymentMethods,
  useSalesChannels,
  useWatchAlias,
} from 'hooks'
import { useEffect, useMemo } from 'react'
import { Item, ItemRestriction, ListOperator, OPERATORS } from 'typings'
import { useFormStateContext } from '../../../../providers'
import { FormAliasKeys } from '../../../../providers/useFormAlias.constate'

type Props = {
  name: string
}

export function getSalesChannelsIds(
  items: Item[] | undefined,
  operator: ListOperator | undefined,
  options: Item[] | undefined
): string[] {
  const itemsIds = getIds(items)
  const optionsIds = getIds(options)

  switch (operator) {
    case OPERATORS.EQUALS:
      return itemsIds.length ? itemsIds : optionsIds
    case OPERATORS.DIFFERENT:
      return optionsIds.filter((id) => !itemsIds.includes(id))
    default:
      return optionsIds
  }
}

export function usePaymentMethodsOptions(props: Props) {
  const { name } = props

  const { form } = useFormStateContext()

  const paymentMethods: Item[] | undefined = useWatch({ form, name })

  const selectedSalesChannel = useWatchAlias<ItemRestriction>({
    form,
    key: FormAliasKeys.salesChannels,
  })

  const { data: salesChannelOptions, isLoading: loadingSalesChannels } =
    useSalesChannels()

  const salesChannelsIds = useMemo(
    () =>
      getSalesChannelsIds(
        selectedSalesChannel?.items,
        selectedSalesChannel?.operator,
        salesChannelOptions
      ),
    [selectedSalesChannel, salesChannelOptions]
  )

  const {
    data: options,
    isLoading: loadingPaymentMethods,
    hasPermissionError,
  } = usePaymentMethods({
    salesChannelsIds,
    options: { enabled: !loadingSalesChannels },
  })

  const optionsIds = useMemo(
    () => options?.map((option) => option.id) ?? [],
    [options]
  )

  useEffect(() => {
    if (
      !paymentMethods ||
      loadingSalesChannels ||
      loadingPaymentMethods ||
      hasPermissionError
    )
      return

    const validPaymentMethods = paymentMethods.filter((method) =>
      optionsIds.includes(method.id)
    )

    if (validPaymentMethods.length === paymentMethods.length) return

    form.setValue(
      name,
      validPaymentMethods.length ? validPaymentMethods : undefined
    )
  }, [
    form,
    loadingPaymentMethods,
    loadingSalesChannels,
    name,
    optionsIds,
    paymentMethods,
    hasPermissionError,
  ])

  return { options, loading: loadingPaymentMethods, hasPermissionError }
}
